import { useId, useState, type JSX } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { revealUp, inViewOnce, useRevealGate } from './anim';
import { SectionGlow } from './SectionGlow';

const FAQS = [
  {
    q: 'Is it free to start?',
    a: 'Yes. Create a room and bring your team in for free — you only ever pay for cosmetic extras, never for the things that matter.',
  },
  {
    q: 'Do my friends need an account?',
    a: 'No. Anyone with your room link can walk in as a guest in seconds — just pick a name and a character. No sign-up, no download.',
  },
  {
    q: "What makes it a 'workspace MMORPG'?",
    a: 'Your company is a guild with its own office and traditions. You earn recognition from teammates — given by people, never a score — and finishing real work triggers real celebrations the whole guild feels. It is about belonging, not monitoring.',
  },
  {
    q: 'How many people can be in a room?',
    a: 'Plenty — whole teams hang out together, spread across lounges, focus zones and meeting rooms. Voice stays crisp because it is room-based: you hear everyone in your room, clear and equal — close a door for a private huddle.',
  },
  {
    q: 'Is my screen and voice private?',
    a: 'Your voice and screen reach the people around you in the room — close a door for a private huddle, and step away to drop out. We never monitor, score, or track your activity.',
  },
  {
    q: 'Does it work on my computer?',
    a: 'It runs right in your browser — nothing to install. Desktops and laptops get the full experience; tablets work great too.',
  },
  {
    q: 'Is this an employee monitoring tool?',
    a: 'Absolutely not. There are no productivity scores, no "time online" leaderboards, no surveillance. It is a place to enjoy being together — that is the whole point.',
  },
] as const;

/**
 * FAQ — an accessible accordion. Each header is a real `<button>` carrying `aria-expanded` +
 * `aria-controls`, and its panel is a `region` labelled by the button (so keyboard + screen-reader users
 * operate it fully). The panel height animates open/closed; under reduced motion it just toggles visibility.
 */
export function FaqSection(): JSX.Element {
  // `reveal` gates the heading's entrance rise (also true during SSG prerender / boot-over-
  // prerender); `reduce` stays the device preference alone for the open/close INTERACTION inside
  // FaqItem, which should keep animating even when the intro is skipped.
  const reveal = useRevealGate();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" aria-labelledby="faq-heading" className="relative mx-auto max-w-3xl px-4 py-24 sm:px-6 sm:py-28">
      <SectionGlow tone="cool" side="left" />
      <motion.div
        variants={reveal ? undefined : revealUp}
        initial={reveal ? false : 'hidden'}
        whileInView={reveal ? undefined : 'show'}
        viewport={inViewOnce}
        className="text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-300/80">Good to know</p>
        <h2 id="faq-heading" className="mt-4 text-balance text-3xl font-bold tracking-tight text-white sm:text-5xl">
          Questions, answered
        </h2>
      </motion.div>

      <div className="mt-12 space-y-3">
        {FAQS.map((f, i) => (
          <FaqItem key={f.q} q={f.q} a={f.a} isOpen={open === i} onToggle={() => setOpen(open === i ? null : i)} reduce={!!reduce} />
        ))}
      </div>
    </section>
  );
}

function FaqItem({
  q,
  a,
  isOpen,
  onToggle,
  reduce,
}: {
  q: string;
  a: string;
  isOpen: boolean;
  onToggle: () => void;
  reduce: boolean;
}): JSX.Element {
  const btnId = useId();
  const panelId = useId();
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <h3>
        <button
          id={btnId}
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left outline-none transition hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-300/70"
        >
          <span className="text-base font-semibold text-white">{q}</span>
          <span
            aria-hidden="true"
            className={`shrink-0 text-amber-300 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}
          >
            +
          </span>
        </button>
      </h3>
      {/* Region the button controls. Height animates open (same target with/without motion — only the
          duration differs); reduced-motion just snaps it. When collapsed we mark the panel `inert` so its
          answer text leaves the tab order AND the accessibility tree — `height:0 + overflow-hidden` alone
          keeps the text readable by screen readers, contradicting the button's `aria-expanded={false}`. */}
      <motion.div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        inert={!isOpen ? true : undefined}
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={reduce ? { duration: 0 } : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <p className="px-5 pb-5 text-sm leading-relaxed text-white/65">{a}</p>
      </motion.div>
    </div>
  );
}
